/**
 * 分栏的属性：几栏、各栏宽度、栏间距。栏里放什么在画布上点「+」或在下面的
 * 嵌套列表里加，不在这里。
 */
import { Button, InputNumber, Segmented, Space, Typography } from 'antd';
import type { ColumnsSection } from '../../../contract/types';

interface Props {
  section: ColumnsSection;
  onChange: (s: ColumnsSection) => void;
}

function even(n: number): number[] {
  const w = Math.round((100 / n) * 10) / 10;
  return Array.from({ length: n }, (_, i) => (i === n - 1 ? Math.round((100 - w * (n - 1)) * 10) / 10 : w));
}

export function ColumnsEditor({ section, onChange }: Props) {
  const n = section.columns.length;
  const label = (t: string) => (
    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
      {t}
    </Typography.Text>
  );
  const setCount = (count: number) => {
    if (count === n) return;
    // 减栏：被砍掉的栏里的区块并进最后一栏，不能悄悄丢
    const columns = section.columns.slice(0, count);
    if (count < n) {
      const spill = section.columns.slice(count).flat();
      columns[count - 1] = [...(columns[count - 1] ?? []), ...spill];
    }
    while (columns.length < count) columns.push([]);
    onChange({ ...section, columns, widths: even(count) });
  };
  const setWidth = (i: number, v: number) => {
    // 改一栏，差额从右边邻居补（最后一栏从左边）
    const j = i < n - 1 ? i + 1 : i - 1;
    if (j < 0) return;
    const total = section.widths[i] + section.widths[j];
    const next = Math.max(10, Math.min(total - 10, v));
    const widths = section.widths.map((w, k) => (k === i ? next : k === j ? total - next : w));
    onChange({ ...section, widths: widths.map((w) => Math.round(w * 10) / 10) });
  };
  return (
    <div>
      <Space size={[12, 8]} wrap align="start">
        <div>
          {label('栏数')}
          <br />
          <Segmented
            size="small"
            value={n}
            options={[2, 3, 4].map((v) => ({ label: `${v} 栏`, value: v }))}
            onChange={(v) => setCount(Number(v))}
          />
        </div>
        <div>
          {label('栏间距(mm)')}
          <br />
          <InputNumber
            size="small"
            min={0}
            max={20}
            value={section.gapMm ?? 4}
            style={{ width: 72 }}
            onChange={(v) => onChange({ ...section, gapMm: v ?? 4 })}
          />
        </div>
        <div>
          {label('各栏宽度(%)')}
          <br />
          <Space size={4}>
            {section.widths.map((w, i) => (
              <InputNumber
                key={i}
                size="small"
                min={10}
                max={90}
                value={w}
                style={{ width: 64 }}
                onChange={(v) => typeof v === 'number' && setWidth(i, v)}
              />
            ))}
            <Button size="small" onClick={() => onChange({ ...section, widths: even(n) })}>
              均分
            </Button>
          </Space>
        </div>
      </Space>
      <Typography.Paragraph type="secondary" style={{ fontSize: 11, margin: '8px 0 0' }}>
        往栏里放内容：画布上点那一栏的「+」，或在下面的栏列表里添加。
      </Typography.Paragraph>
    </div>
  );
}
